import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Calendar } from '@/components/ui/calendar';
import { Filter, Calendar as CalendarIcon, Star, RotateCcw, Clock, Users } from 'lucide-react';
import { format } from 'date-fns';

export interface SimpleFilterCriteria {
  search: string;
  leadScore: string; // 'all', '5', '4', '3', '2', '1', '0'
  source: string;
  recentLeads: string; // 'all', 'today', 'week', 'last30days', 'month'
  needsContact: string; // 'all', 'never_contacted', 'needs_followup', 'missing_phone'
  sortBy: 'leadScore' | 'dateAdded' | 'name' | 'dealValue' | 'stageUpdated' | 'nextContact';
  sortOrder: 'asc' | 'desc';
}

interface SimplePipelineFiltersProps {
  filters: SimpleFilterCriteria;
  onFiltersChange: (filters: SimpleFilterCriteria) => void;
  customerCount: number;
  totalCustomers: number;
}

const defaultFilters: SimpleFilterCriteria = {
  search: '',
  leadScore: 'all',
  source: 'all',
  recentLeads: 'all',
  needsContact: 'all',
  sortBy: 'dateAdded',
  sortOrder: 'asc',
};

export function SimplePipelineFilters({
  filters,
  onFiltersChange,
  customerCount,
  totalCustomers,
}: SimplePipelineFiltersProps) {
  const [isOpen, setIsOpen] = useState(false);

  const handleFilterChange = (key: keyof SimpleFilterCriteria, value: any) => {
    const newFilters = { ...filters, [key]: value };
    onFiltersChange(newFilters);
  };

  const resetFilters = () => {
    onFiltersChange(defaultFilters);
  };

  const hasActiveFilters = Object.entries(filters).some(([key, value]) => {
    if (key === 'search') return value !== '';
    if (key === 'sortBy') return value !== 'dateAdded';
    if (key === 'sortOrder') return value !== 'asc';
    return value !== 'all';
  });

  return (
    <div className="mb-6">
      <Popover open={isOpen} onOpenChange={setIsOpen}>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            className={`h-12 px-6 rounded-2xl border-2 transition-all duration-300 ${
              hasActiveFilters
                ? 'border-emerald-400 bg-emerald-50 dark:bg-emerald-950/30 text-emerald-700 dark:text-emerald-300'
                : 'border-slate-200 dark:border-slate-700'
            }`}
          >
            <Filter className="mr-2 h-5 w-5" />
            Filters
            {hasActiveFilters && (
              <Badge
                variant="secondary"
                className="ml-2 bg-emerald-100 dark:bg-emerald-900 text-emerald-700 dark:text-emerald-300"
              >
                Active
              </Badge>
            )}
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-80 p-0" align="start">
          <Card className="border-0 shadow-xl">
            <CardHeader className="pb-4">
              <CardTitle className="flex items-center justify-between">
                <span className="flex items-center gap-2">
                  <Filter className="h-5 w-5" />
                  Quick Filters
                </span>
                <div className="flex items-center gap-2">
                  <Badge variant="outline" className="text-xs">
                    {customerCount} of {totalCustomers}
                  </Badge>
                  {hasActiveFilters && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={resetFilters}
                      className="h-8 w-8 p-0"
                    >
                      <RotateCcw className="h-4 w-4" />
                    </Button>
                  )}
                </div>
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Lead Score Filter */}
              <div>
                <Label className="text-sm font-semibold mb-2 block flex items-center gap-2">
                  <Star className="h-4 w-4 text-yellow-500" />
                  Lead Quality
                </Label>
                <Select
                  value={filters.leadScore}
                  onValueChange={(value) => handleFilterChange('leadScore', value)}
                >
                  <SelectTrigger className="text-xs">
                    <SelectValue placeholder="All ratings" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All ratings</SelectItem>
                    <SelectItem value="5">⭐⭐⭐⭐⭐ Hot leads</SelectItem>
                    <SelectItem value="4">⭐⭐⭐⭐ Good leads</SelectItem>
                    <SelectItem value="3">⭐⭐⭐ Medium leads</SelectItem>
                    <SelectItem value="2">⭐⭐ Low leads</SelectItem>
                    <SelectItem value="1">⭐ Poor leads</SelectItem>
                    <SelectItem value="0">No rating yet</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Lead Source */}
              <div>
                <Label className="text-sm font-semibold mb-2 block flex items-center gap-2">
                  <Users className="h-4 w-4 text-purple-500" />
                  Source
                </Label>
                <Select
                  value={filters.source}
                  onValueChange={(value) => handleFilterChange('source', value)}
                >
                  <SelectTrigger className="text-xs">
                    <SelectValue placeholder="All sources" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All sources</SelectItem>
                    <SelectItem value="Meta">Meta (Facebook/Instagram)</SelectItem>
                    <SelectItem value="manual">Manual entry</SelectItem>
                    <SelectItem value="opensolar">OpenSolar</SelectItem>
                    <SelectItem value="null">Unknown source</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Recent Leads */}
              <div>
                <Label className="text-sm font-semibold mb-2 block flex items-center gap-2">
                  <Clock className="h-4 w-4 text-blue-500" />
                  Added When
                </Label>
                <Select
                  value={filters.recentLeads}
                  onValueChange={(value) => handleFilterChange('recentLeads', value)}
                >
                  <SelectTrigger className="text-xs">
                    <SelectValue placeholder="All time" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All time</SelectItem>
                    <SelectItem value="today">Today</SelectItem>
                    <SelectItem value="week">This week</SelectItem>
                    <SelectItem value="last30days">Last 30 days</SelectItem>
                    <SelectItem value="month">This month</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Contact Status */}
              <div>
                <Label className="text-sm font-semibold mb-2 block flex items-center gap-2">
                  <CalendarIcon className="h-4 w-4 text-green-500" />
                  Contact Status
                </Label>
                <Select
                  value={filters.needsContact}
                  onValueChange={(value) => handleFilterChange('needsContact', value)}
                >
                  <SelectTrigger className="text-xs">
                    <SelectValue placeholder="All customers" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All customers</SelectItem>
                    <SelectItem value="never_contacted">Never contacted</SelectItem>
                    <SelectItem value="needs_followup">Needs follow-up</SelectItem>
                    <SelectItem value="missing_phone">Phone missing</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Sort Options */}
              <div>
                <Label className="text-sm font-semibold mb-2 block">Sort By</Label>
                <div className="flex gap-2">
                  <Select
                    value={filters.sortBy}
                    onValueChange={(value: any) => handleFilterChange('sortBy', value)}
                  >
                    <SelectTrigger className="text-xs">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="leadScore">Lead Score</SelectItem>
                      <SelectItem value="dateAdded">Date Added</SelectItem>
                      <SelectItem value="name">Name</SelectItem>
                      <SelectItem value="dealValue">Deal Value</SelectItem>
                      <SelectItem value="stageUpdated">Stage Updated</SelectItem>
                      <SelectItem value="nextContact">Next Contact</SelectItem>
                    </SelectContent>
                  </Select>
                  <Select
                    value={filters.sortOrder}
                    onValueChange={(value: any) => handleFilterChange('sortOrder', value)}
                  >
                    <SelectTrigger className="text-xs w-24">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="asc">
                        {filters.sortBy === 'dateAdded' || filters.sortBy === 'stageUpdated'
                          ? 'Oldest First'
                          : filters.sortBy === 'name'
                            ? 'A to Z'
                            : filters.sortBy === 'nextContact'
                              ? 'Soonest First'
                              : 'Low to High'}
                      </SelectItem>
                      <SelectItem value="desc">
                        {filters.sortBy === 'dateAdded' || filters.sortBy === 'stageUpdated'
                          ? 'Newest First'
                          : filters.sortBy === 'name'
                            ? 'Z to A'
                            : filters.sortBy === 'nextContact'
                              ? 'Furthest First'
                              : 'High to Low'}
                      </SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </CardContent>
          </Card>
        </PopoverContent>
      </Popover>
    </div>
  );
}
